import GoogleMapReact, { type ChangeEventValue } from 'google-map-react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import type { DeliveryZone } from '~/api/DeliveryAddress';
import cdConfig from '~/assets/mapStyle/cd.json';
import localeeConfig from '~/assets/mapStyle/localee.json';
import { company } from '~/company/Company';
import type { DeliveryZoneRef } from '~/components/ModalDeliveryAddress/GMap/interfaces';
import { userStore } from '~/stores';
import { GOOGLE_API_KEY } from '~/stores/constants';

import {
  deliveryAddressStore,
  mapCenter,
  mapZoom,
} from '../DeliveryAddressStore';

import { PudoMarker } from './PudoMarker/PudoMarker';
import { createPolygonForZone, getZoneCentroid } from './utils';

const mapStyle = company.match({
  citydrinks: cdConfig,
  default: localeeConfig,
});

interface GMapProps {
  draggable?: boolean;
  withDefaultCenter?: boolean;
  onPudoSelect?: (center: { lat: number; lng: number }) => void;
  onPudoPopupChange?: (isOpen: boolean) => void;
}

interface PudoZoneData {
  zone: DeliveryZone;
  center: { lat: number; lng: number };
}

const GMap = ({
  draggable = true,
  withDefaultCenter = true,
  onPudoSelect,
  onPudoPopupChange,
}: GMapProps) => {
  const [defaultCenter] = useState<GeoCoordinates>(
    () => deliveryAddressStore.mapCenter,
  );
  const [pudoZones, setPudoZones] = useState<PudoZoneData[]>([]);
  const [openPudoName, setOpenPudoName] = useState<string | null>(null);

  const closePudoPopup = () => {
    if (openPudoName) {
      setOpenPudoName(null);
      onPudoPopupChange?.(false);
    }
  };

  const handleMapChange = (e: ChangeEventValue) => {
    if (deliveryAddressStore.isActiveGeolocation) {
      return;
    }

    if (
      (e.center.lat.toFixed(7) === mapCenter.lat.toFixed(7) &&
        e.center.lng.toFixed(7) === mapCenter.lng.toFixed(7)) ||
      !Object.keys(toJS(deliveryAddressStore.renderedZonesOnMap)).length
    ) {
      return;
    }

    deliveryAddressStore.selectZoneByCoordinates(e.center);
    deliveryAddressStore.setMapZoom(e.zoom);
  };

  const handleMapAction = (flag: boolean, shouldDropGeolocation: boolean) => {
    closePudoPopup();
    deliveryAddressStore.setIsMapBusy(flag);

    if (shouldDropGeolocation) {
      /**
       * Because "handleMapAction" is called when after zoom animation,
       * it is drop geolocation flag, and rewrite map center coordinates
       * To avoid it, we need to drop geolocation flag only when user drag map,
       * but not when map was just zoomed
       * */
      deliveryAddressStore.setIsActiveGeolocation(false);
    }
    deliveryAddressStore.setIsManuallyInput(false);
    deliveryAddressStore.setInputAddressFocused(false);
    deliveryAddressStore.setIsAddressNotCovered(false);
    deliveryAddressStore.setIsAddressNotExist(false);
  };

  const handleMapClick = () => {
    closePudoPopup();
    deliveryAddressStore.setIsManuallyInput(false);
    deliveryAddressStore.setInputAddressFocused(false);
    if (document.activeElement) {
      const activeEl: HTMLInputElement =
        document.activeElement as HTMLInputElement;
      activeEl.blur();
    }
  };

  const handleApiLoaded = async ({ map }: { map: google.maps.Map }) => {
    try {
      const coverages = await userStore.getDeliveryZones();
      if (!coverages.length) {
        return;
      }

      const polygonsRef: Record<string, DeliveryZoneRef> = {};

      coverages.forEach((el) => {
        new Map(
          el.zones
            .sort((a, b) => a.priority - b.priority)
            .map((zone) => {
              zone.warehouseId = el.warehouse.id;
              return [zone.name, zone];
            }),
        ).forEach((zone) => {
          const polygon = createPolygonForZone(zone);
          polygon.setMap(map);

          const zoneRef: DeliveryZoneRef = {
            zone,
            polygon,
          };

          if (zone.warehouseId) {
            polygonsRef[`${zone.name}-${zone.warehouseId}`] = zoneRef;
          }
        });
      });

      deliveryAddressStore.renderedZonesOnMap = polygonsRef;

      const pudoData: PudoZoneData[] = [];
      for (const ref of Object.values(polygonsRef)) {
        if (ref.zone.displayType === 'pudo') {
          pudoData.push({
            zone: ref.zone,
            center: getZoneCentroid(ref.zone),
          });
        }
      }
      setPudoZones(pudoData);

      if (userStore.isAuthorized) {
        await userStore.fetchAddresses();
      }
    } catch (error) {
      error && console.error(error);
    }
  };

  const center: GeoCoordinates =
    deliveryAddressStore.isActiveGeolocation &&
    deliveryAddressStore.geolocationCoordinates
      ? deliveryAddressStore.geolocationCoordinates
      : deliveryAddressStore.mapCenter;

  if (defaultCenter === null) {
    return <></>;
  }

  return (
    <GoogleMapReact
      yesIWantToUseGoogleMapApiInternals
      bootstrapURLKeys={{
        key: GOOGLE_API_KEY,
        language: 'en-GB',
        region: 'GB',
      }}
      defaultCenter={defaultCenter}
      defaultZoom={mapZoom}
      zoom={deliveryAddressStore.mapZoom}
      center={withDefaultCenter ? center : undefined}
      options={{
        clickableIcons: false,
        disableDefaultUI: true,
        gestureHandling: 'greedy',
        styles: mapStyle,
        draggable,
      }}
      onGoogleApiLoaded={handleApiLoaded}
      shouldUnregisterMapOnUnmount={true}
      onChange={handleMapChange}
      onClick={handleMapClick}
      onDrag={() => handleMapAction(true, true)}
      onDragEnd={() => handleMapAction(false, true)}
      onZoomAnimationStart={() => handleMapAction(true, false)}
      onZoomAnimationEnd={() => handleMapAction(false, false)}
      style={{
        pointerEvents: deliveryAddressStore.isChangeDeliveryAreaPopover
          ? 'none'
          : 'auto',
      }}
    >
      {pudoZones.map((pudo) => (
        <PudoMarker
          key={pudo.zone.name}
          lat={pudo.center.lat}
          lng={pudo.center.lng}
          name={pudo.zone.name}
          description={pudo.zone.description}
          color={pudo.zone.color}
          isOpen={openPudoName === pudo.zone.name}
          isLoading={deliveryAddressStore.isLoading}
          onToggle={() => {
            const next =
              openPudoName === pudo.zone.name ? null : pudo.zone.name;
            setOpenPudoName(next);
            onPudoPopupChange?.(next !== null);
          }}
          onSelect={() => onPudoSelect?.(pudo.center)}
        />
      ))}
    </GoogleMapReact>
  );
};

export default observer(GMap);
